/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

/**
 * ONE canonical name per country, everywhere a name is looked up, matched or
 * displayed. Name is not an identifier: "Republic of Mauritius" missed the
 * supervisory-effectiveness dataset that holds "Mauritius", so a rated
 * jurisdiction read as never assessed; "UAE" and "United Arab Emirates" sat
 * side by side on the same geography line; "England and Wales" duplicated
 * "United Kingdom" (all observed on production, 07/08/26). Every consumer
 * canonicalises through here BEFORE normalising, deduplicating, or indexing.
 *
 * The long-form stripper is deliberately conservative: "Republic of X"
 * reduces to X only when the full form is NOT itself a distinct entry.
 * "Democratic Republic of the Congo" and "Dominican Republic" are canonical
 * names in their own right and must never be mangled.
 */

const ALIASES = new Map(Object.entries({
  'uae': 'United Arab Emirates',
  'u a e': 'United Arab Emirates',
  'uk': 'United Kingdom',
  'u k': 'United Kingdom',
  'great britain': 'United Kingdom',
  'britain': 'United Kingdom',
  'england': 'United Kingdom',
  'england and wales': 'United Kingdom',
  'scotland': 'United Kingdom',
  'wales': 'United Kingdom',
  'northern ireland': 'United Kingdom',
  'usa': 'United States',
  'u s a': 'United States',
  'us': 'United States',
  'united states of america': 'United States',
  'america': 'United States',
  'holland': 'Netherlands',
  'the netherlands': 'Netherlands',
  'czechia': 'Czech Republic',
  'uae dubai': 'United Arab Emirates',
  'dubai': 'United Arab Emirates',
  'abu dhabi': 'United Arab Emirates',
  'hong kong sar': 'Hong Kong',
  'hksar': 'Hong Kong',
  'macau': 'Macao',
  'prc': 'China',
  "people's republic of china": 'China',
  'peoples republic of china': 'China',
  'republic of korea': 'South Korea',
  'korea republic of': 'South Korea',
  'dprk': 'North Korea',
  "democratic people's republic of korea": 'North Korea',
  'democratic peoples republic of korea': 'North Korea',
  'russian federation': 'Russia',
  'burma': 'Myanmar',
  'ivory coast': "Cote d'Ivoire",
  'swaziland': 'Eswatini',
  'cape verde': 'Cabo Verde',
  'turkiye': 'Turkey',
  'viet nam': 'Vietnam',
  'brunei darussalam': 'Brunei',
  'bvi': 'British Virgin Islands',
  // v1_979: the FATF names the territory "Virgin Islands (UK)". Read whole,
  // before any bracket is cut (see canonicalPlace), so the FATF's own name,
  // the ISO short name and the common forms all reach the FATF lists.
  'b v i': 'British Virgin Islands',
  'virgin islands uk': 'British Virgin Islands',
  'uk virgin islands': 'British Virgin Islands',
  'virgin islands british': 'British Virgin Islands',
  'virgin islands of united kingdom': 'British Virgin Islands',
  'tortola': 'British Virgin Islands',
  'virgin islands us': 'US Virgin Islands',
  'virgin islands u s': 'US Virgin Islands',
  'united states virgin islands': 'US Virgin Islands',
  'usvi': 'US Virgin Islands',
  'congo kinshasa': 'Democratic Republic of the Congo',
  'congo brazzaville': 'Republic of the Congo',
  'korea north': 'North Korea',
  'korea dprk': 'North Korea',
  "korea democratic people's republic of": 'North Korea',
  'korea democratic peoples republic of': 'North Korea',
  'drc': 'Democratic Republic of the Congo',
  'uk crown dependency of jersey': 'Jersey',
  'kingdom of saudi arabia': 'Saudi Arabia',
  'ksa': 'Saudi Arabia'
}));

/** Long forms that are themselves canonical and must never be reduced.
 * Folded through the SAME key function as the input at module init, so
 * "the"-stripping and punctuation folding can never desynchronise the two
 * sides of the comparison. */
const PROTECTED_LONG_FORM_NAMES = [
  'Dominican Republic',
  'Democratic Republic of the Congo',
  'Republic of the Congo',
  'Central African Republic',
  'Czech Republic'
];

let PROTECTED_LONG_FORMS; // initialised after foldKey below

const foldKey = (v) => String(v || '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9']+/g, ' ')
  .replace(/\bthe\b/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

PROTECTED_LONG_FORMS = new Set(PROTECTED_LONG_FORM_NAMES.map(foldKey));

/**
 * Canonicalise a place name for lookup, matching and display.
 * Returns the canonical display form, or the cleaned input when no rule
 * applies (unknown names pass through unchanged; this never invents places).
 */
// v1_837: a trailing qualifier ("United Kingdom only", "Switzerland mainly")
// is the board's prose, not part of the name, and must not stop a country
// being recognised or matched.
const TRAILING_QUALIFIER = /(?:\s+(?:only|mainly|primarily|predominantly|principally|chiefly|largely|mostly|exclusively|solely))+\s*$/i;

export function canonicalPlace(v) {
  // v1_979: the whole name first, brackets included. Cutting the bracket first
  // turned the FATF's own "Virgin Islands (UK)" into "Virgin Islands", which
  // is on no list, and "Congo (Kinshasa)" into the other Congo.
  const wholeKey = foldKey(String(v || '').replace(/\s+/g, ' ').trim().replace(TRAILING_QUALIFIER, '').trim());
  if (wholeKey && ALIASES.has(wholeKey)) return ALIASES.get(wholeKey);
  const cleaned = String(v || '').replace(/\s*\(.*$/, '').replace(/\s+/g, ' ').trim().replace(TRAILING_QUALIFIER, '').trim();
  if (!cleaned) return '';
  const key = foldKey(cleaned);
  if (ALIASES.has(key)) return ALIASES.get(key);
  if (PROTECTED_LONG_FORMS.has(key)) return cleaned;
  // "Republic of X" / "Kingdom of X" / "State of X" and qualified variants
  // reduce to X only when the full form is not protected above.
  const m = cleaned.match(/^(?:the\s+)?(?:islamic\s+|federal\s+|federative\s+|democratic\s+|socialist\s+|arab\s+|co-?operative\s+|plurinational\s+|bolivarian\s+|oriental\s+|united\s+)?(?:republic|kingdom|state|sultanate|principality|emirate|commonwealth|grand\s+duchy|federation)\s+of\s+(.+)$/i);
  if (m) {
    const inner = m[1].replace(/\s+/g, ' ').trim();
    const innerKey = foldKey(inner);
    if (ALIASES.has(innerKey)) return ALIASES.get(innerKey);
    return inner.replace(/^\w/, c => c.toUpperCase());
  }
  return cleaned;
}

/**
 * Clause-level prohibition guard for prose fields. A country named inside a
 * prohibition, exclusion or sanctions-policy clause is the client's OWN
 * CONTROL, not their exposure: a Belgian PSP whose AML policy "prohibits
 * dealings with North Korea, Iran, Russia, Myanmar and Belarus" was scored
 * as exposed to all five, gated and floored to High (production, 07/08/26).
 * Clauses split on ; . and the word "excluding"/"except", and any clause
 * carrying a prohibition marker contributes nothing.
 */
const PROHIBITION_MARKERS = /\b(prohibit\w*|proscri\w*|exclu\w*|except\b|sanction\w*|embargo\w*|restrict\w*|forbid\w*|bans?\b|bann\w*|blacklist\w*|denie[ds]\b|deny\w*|do(?:es)?\s+not\s+(?:serve|deal|transact|accept|onboard|operate|offer)|no\s+(?:dealings?|business|clients?|exposure|transactions?)|not\s+(?:served?|accepted|permitted|allowed|available)|never\s+(?:serve|deal|transact)\w*|none\s+declared|will\s+not|avoid\w*)\b/i;

export function splitPermittedClauses(text) {
  const out = [];
  for (const clause of String(text || '').split(/[;.]|\b(?:excluding|except(?:ing)?)\b/i)) {
    const c = clause.trim();
    if (!c) continue;
    if (!PROHIBITION_MARKERS.test(c)) { out.push(c); continue; }
    // A marker-carrying clause is cut at the marker: segments BEFORE it are
    // permitted content ("United Kingdom, Belize, [no monies from sanctioned
    // countries]"), the marker segment and everything AFTER it are the
    // prohibition and its object list ("policy prohibits dealings with North
    // Korea, Iran, Russia") and contribute nothing. Cutting the tail is
    // deliberately conservative: a prohibition verb governs the list that
    // follows it.
    const segs = c.split(',');
    const kept = [];
    for (const seg of segs) {
      if (PROHIBITION_MARKERS.test(seg)) break;
      if (seg.trim()) kept.push(seg.trim());
    }
    if (kept.length) out.push(kept.join(', '));
  }
  return out;
}

export default canonicalPlace;
